import api from "api"
import { CartlessOrder } from "api/resources/orders/types"
import CourseFallback from "assets/Placeholder/product-image-placeholder.svg"
import { getPgCTLProductImage } from "features/LearningItems/getProductDisplayConfig"
import { usePayment } from "features/Payment/hooks/usePayment"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { mapErrorsV2 } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

type Props = {
  purchaseDetails: {
    itemName: string
    amount: number
    currency: {
      code: string
      symbol: string
    }
  } | null
  onSuccessfulCompletion: () => void
}

export const usePgctlPayment = (props: Props) => {
  const [stripeForm, setStripeForm] = useState<JSX.Element | null>(null)

  const [order, setOrder] = useState<CartlessOrder | null>(null)

  const enrollments = useEnrollments()

  const onSuccess = async () => {
    const handleClose = () => {
      enrollments.refetch()
    }

    props.onSuccessfulCompletion()

    handleClose()
  }

  const [params] = useSearchParams()
  const productSlug = params.get("product")

  const isFromLandingPage = params.get("source") === "landing-page"

  const { checkout } = usePayment({
    stripeConfig: {
      paymentKey: import.meta.env.VITE_STRIPE_PAYMENT_KEY,

      logo: () => (
        <img
          onError={({ currentTarget }) => {
            currentTarget.onerror = null
            currentTarget.src = CourseFallback
          }}
          src={getPgCTLProductImage(productSlug!, {
            height: 56,
            width: 56,
          })}
          alt="logo"
          className="me-1.5 size-7 rounded-lg"
        />
      ),
      getForm: setStripeForm,
      handleClose: () => {
        window.location.reload()
        setStripeForm(null)
      },
      redirectURL: window.location.href,
    },
    razorpayConfig: {
      logo: getPgCTLProductImage(productSlug!, {
        height: 48,
        width: 48,
      }),
      successHandler: () => {
        onSuccess()
      },
    },
    purchaseDetails: props.purchaseDetails,
  })

  const executePaymentFlow = async (
    billingAddressId: string,
    productSlug: string
  ) => {
    try {
      const res = await api.orders.createOrder({
        data: {
          source: isFromLandingPage ? "direct_enrollment_form" : undefined,
          productSlug,
          billingAddressId,
          registrationRequired: true,
        },
      })

      if (res.paymentPlans.length === 0) {
        throw new Error("No payment plans found")
      }

      setOrder(res)

      const paymentPlanId = res.paymentPlans[0].id

      await checkout(paymentPlanId, {
        promocode: null,
      })

      return res
    } catch (error) {
      mapErrorsV2(error)
    }
  }

  return {
    executePaymentFlow,
    stripePaymentElement: stripeForm,
    orderId: order?.id,
  }
}
